import React from 'react';
import { Link } from 'react-router-dom';
import { getPublicContactEmail } from '../../../utils/publicContactEmail.js';

const LAST_UPDATED = '2026年10月8日';

const PPC_FOREIGN_SYSTEM_URL = 'https://www.ppc.go.jp/personalinfo/legal/kaiseihogohou/#gaikoku';

/**
 * 外国にある第三者への提供に関する公表事項。
 *
 * 個人情報保護法 第28条第3項および施行規則 第18条第3項に基づく情報提供として、
 * 利用規約 第12条の7、プライバシーポリシー 4.3・7.1、患者向け同意文書「個人情報の
 * 利用に関する許諾」第4項が参照している固定 URL（/crossborder）。
 * 掲載内容の原本は正本フォルダ `08_個人情報利用に関して/基準適合体制の確認記録.md`。
 * 提供先を追加・変更するときは、原本と本ページの両方を必ず更新する。
 */
const CrossBorderDisclosurePage = ({
  backTo = '/',
  backLabel = 'トップに戻る',
  embedded = false,
} = {}) => {
  const currentYear = new Date().getFullYear();
  const contactEmail = getPublicContactEmail();
  const pageClassName = embedded ? 'hub-legal-page hub-legal-page--embedded' : 'hub-legal-page';

  return (
    <div className={pageClassName}>
      <div className="hub-legal-container">
        <div className="hub-legal-header">
          <Link to={backTo} className="hub-legal-back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            {backLabel}
          </Link>
          <h1>外国にある第三者への提供に関する公表事項</h1>
          <p className="hub-legal-updated">最終更新日: {LAST_UPDATED}</p>
        </div>

        <div className="hub-legal-content">
          <section>
            <h2>1. このページについて</h2>
            <p>
              株式会社ConvergePartners（以下「当社」といいます）は、当社が提供する Synapse Platform
              および Synapseシリーズ（以下「本サービス」といいます）の運営にあたり、外国にある事業者の
              提供するクラウドサービス等を利用しています。
            </p>
            <p>
              本ページは、個人情報の保護に関する法律（以下「法」といいます）第28条第3項および
              同法施行規則（以下「規則」といいます）第18条第3項に基づく情報提供として、
              外国にある第三者への個人データの提供に関する事項を公表するものです。
              利用規約 第12条の7、<Link to="/privacy">プライバシーポリシー</Link> 4.3 および 7.1、
              ならびに医療機関を通じて患者さまにご確認いただく「個人情報の利用に関する許諾」第4項が、
              本ページを参照しています。
            </p>
          </section>

          <section>
            <h2>2. 取扱いの所在地の原則</h2>
            <p>
              本サービスが取り扱う患者さまの診療に関する情報は、<strong>日本国内のサーバー（東京リージョン）に
              保存します。人工知能による解析も、日本国内のリージョンで実行します。</strong>
              当社は、患者データを取り扱う処理について、リージョンを日本国内に固定することを
              社内規則として定めています。
            </p>
            <p>
              一方、当社が利用するクラウドサービス等の提供事業者には、外国に本店を置く法人が含まれます。
              このため、データを保存・処理する設備が日本国内にある場合であっても、これらの事業者は
              法律上「外国にある第三者」として個人データを取り扱うことになります。本ページは、
              その事業者について公表するものです。
            </p>
          </section>

          <section>
            <h2>3. 提供先の一覧</h2>
            <div className="hub-legal-table-wrap">
              <table className="hub-legal-table">
                <thead>
                  <tr>
                    <th>事業者</th>
                    <th>所在する外国</th>
                    <th>提供する個人データ</th>
                    <th>相当措置の根拠</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Google（Google Cloud Platform、Vertex AI、Firebase、Gmail API、reCAPTCHA）</td>
                    <td>シンガポール共和国、アメリカ合衆国</td>
                    <td>患者さまの診療に関する情報、医療機関および利用者の情報</td>
                    <td>契約（規則第16条第1号）</td>
                  </tr>
                  <tr>
                    <td>Stripe（決済）</td>
                    <td>アイルランド、アメリカ合衆国</td>
                    <td>医療機関（契約者）の名称、メールアドレス、住所</td>
                    <td>国際的な枠組みに基づく認定（規則第16条第2号）</td>
                  </tr>
                  <tr>
                    <td>Slack（社内通知）</td>
                    <td>アイルランド、アメリカ合衆国</td>
                    <td>お問い合わせいただいた方の氏名、メールアドレス、所属、お問い合わせ内容</td>
                    <td>国際的な枠組みに基づく認定（規則第16条第2号）</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              <strong>患者さまの診療に関する情報が渡りうるのは、上記のうち Google のみです。</strong>
              Stripe および Slack へは、患者さまに関する情報を送信していません。
            </p>
          </section>

          <section>
            <h2>4. 提供先ごとの詳細</h2>

            <h3>4.1 Google</h3>
            <ul>
              <li>
                契約主体: 請求先住所が日本の場合、Google Cloud Japan G.K.（グーグル・クラウド・ジャパン合同会社）
                が窓口となりますが、契約上の「Google」は Google Asia Pacific Pte. Ltd.（シンガポール共和国）
                およびその関連会社を指します。サービスデータの主たる管理者は Google LLC（アメリカ合衆国）です。
              </li>
              <li>
                相当措置の概要: Cloud Data Processing Addendum（データ処理に関する追加契約）により、
                Google が当社の指示に従ってのみ個人データを処理すること、再委託先に同等の義務を課すこと、
                安全管理措置を講じること、データの削除・返還および監査に応じることが定められています。
                Google は、個人データが処理される場所にかかわらず日本の個人情報保護法と同等の保護が
                適用されるよう体制を確立している旨を公表しています。
              </li>
              <li>
                提供する個人データ: 患者さまの氏名、カルテ番号、生年月日、性別、問診情報、診療情報、
                検査結果、医療画像、これらから生成されるレポート、ならびに医療機関および利用者の情報。
              </li>
            </ul>

            <h3>4.2 Stripe</h3>
            <ul>
              <li>
                契約主体: Stripe Payments Europe, Limited（アイルランド）。個人データの処理は
                Stripe, Inc./Stripe, LLC（アメリカ合衆国）およびその関連会社が行います。
              </li>
              <li>
                相当措置の概要: Stripe, LLC は Global CBPR および Global PRP の認証を取得しています
                （認証機関 TrustArc、当社確認日 2026年8月29日）。あわせて、日本国内のデータ主体の
                個人データについて、日本の個人情報保護法に基づく権利義務と同等の内容を定めた書面契約を
                締結する旨が Stripe のプライバシーポリシーに定められています。
              </li>
              <li>提供する個人データ: 医療機関（契約者）の名称、メールアドレス、住所、契約識別情報。</li>
            </ul>

            <h3>4.3 Slack</h3>
            <ul>
              <li>
                契約主体: Slack Technologies Limited（アイルランド）。Global CBPR の認証名義は
                Slack Technologies, LLC（アメリカ合衆国）です。
              </li>
              <li>
                相当措置の概要: Slack Technologies, LLC および親会社である Salesforce, Inc. は、
                Global CBPR および Global PRP の認証を取得しています（認証機関 Schellman Compliance, LLC
                および TrustArc、当社確認日 2026年8月29日）。あわせて Slack は標準のデータ処理補遺を
                提供しています。
              </li>
              <li>
                提供する個人データ: 当社お問い合わせフォームからお問い合わせいただいた方の氏名、
                メールアドレス、所属組織名およびお問い合わせ内容。ならびに社内運用上の通知に含まれる
                医療機関名および担当者の識別情報。
              </li>
            </ul>
          </section>

          <section>
            <h2>5. 提供先の所在する外国の個人情報の保護に関する制度</h2>
            <ul>
              <li>
                <strong>アメリカ合衆国:</strong> 分野横断的な包括的個人情報保護法は連邦レベルには存在せず、
                分野別の連邦法および州法によって規律されています。政府機関によるデータへのアクセスに関する
                制度が存在します。
              </li>
              <li>
                <strong>シンガポール共和国:</strong> 個人データ保護法（Personal Data Protection Act）により、
                個人データの収集・利用・開示および国外移転が規律されています。
              </li>
              <li>
                <strong>アイルランド:</strong> EU 一般データ保護規則（GDPR）およびアイルランドの
                データ保護法が適用されます。なお、EU 加盟国に所在する第三者への提供は、規則第15条により
                法第28条の適用対象外とされています。
              </li>
            </ul>
            <p>
              各国の制度の詳細は、個人情報保護委員会が公表する外国における個人情報の保護に関する
              制度等の調査結果をご参照ください。
              <br />
              <a href={PPC_FOREIGN_SYSTEM_URL} target="_blank" rel="noopener noreferrer">
                {PPC_FOREIGN_SYSTEM_URL}
              </a>
            </p>
          </section>

          <section>
            <h2>6. 相当措置の継続的な実施を確保するための措置</h2>
            <p>
              当社は、規則第18条第1項に基づき、次のとおり継続的な確認を行います。
            </p>
            <ul>
              <li>確認の頻度: 年1回以上（毎年8月末を基準日とします）</li>
              <li>
                確認の方法: 提供先による相当措置の実施状況（認証の有効性、データ処理契約の改訂の有無、
                契約主体の変更の有無）、および相当措置の実施に影響を及ぼすおそれのある当該外国の制度の
                有無・内容を確認します。あわせて、当社側で新たな提供先が追加されていないか、および
                リージョン設定が方針どおりかを確認します。
              </li>
              <li>実施者: 登坂 悠也</li>
              <li>直近の確認日: 2026年8月29日</li>
            </ul>
            <p>
              確認の結果、提供先による相当措置の実施に支障が生じていることが判明した場合には、
              必要かつ適切な措置を講じます。支障の解消が困難であるときは、当該提供先への
              個人データの提供を停止します。
            </p>
          </section>

          <section>
            <h2>7. お問い合わせ</h2>
            <p>
              規則第18条第3項に基づき、ご本人からのお求めに応じて、基準適合体制の整備の方法、
              提供先が講ずる相当措置の概要、確認の頻度および方法、提供先の所在する外国の名称および
              当該外国の制度の有無・内容、ならびに相当措置の実施に支障がある場合はその概要と当社が
              講ずる措置について、遅滞なく情報を提供いたします。
            </p>
            <div className="hub-legal-contact">
              <p>株式会社ConvergePartners</p>
              <p>
                〒330-0063 埼玉県さいたま市浦和区高砂一丁目2番1号 エイペックスタワー浦和108
              </p>
              <p>
                メール: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
              </p>
              <p>
                <Link to="/contact">お問い合わせフォーム</Link>
              </p>
            </div>
          </section>
        </div>

        <div className="hub-legal-footer">
          <p>&copy; {currentYear} Synapse by ConvergePartners Inc.</p>
        </div>
      </div>
    </div>
  );
};

export default CrossBorderDisclosurePage;
